class BeatStore {
    constructor() {
        this.currentAudio = null;
        this.currentBeatItem = null;
        this.playerBar = document.getElementById('beatPlayerBar');
        this.playPauseBtn = document.getElementById('playerPlayPause');
        this.playIcon = this.playPauseBtn.querySelector('.play-icon');
        this.pauseIcon = this.playPauseBtn.querySelector('.pause-icon');
        this.progressBar = document.querySelector('.progress-bar');
        this.progressFill = document.querySelector('.progress-fill');
        this.playerTime = document.querySelector('.player-time');
        this.playerTrackName = document.querySelector('.player-track-name');
        this.progressTooltip = document.getElementById('progressTooltip');
        this.playerCartBtn = document.getElementById('playerCartBtn');
        this.playerCartPrice = document.getElementById('playerCartPrice');
        this.licenseModal = document.getElementById('licenseModal');
        this.currentProductData = null;
        this.selectedLicense = null;

        this.volumeBtn = document.getElementById('playerVolumeBtn');
        this.volumeSliderTrack = document.getElementById('volumeSliderTrack');
        this.volumeSliderFill = document.getElementById('volumeSliderFill');
        
        this.volumeIcons = {
            normal: document.getElementById('volume-icon-normal'),
            mute: document.getElementById('volume-icon-mute')
        };
        
        this.volumeStorageKey = 'beat_store_player_volume';
        this.lastVolume = this.getStoredVolume(); 
        this.isDraggingVolume = false; 
        this.isMuted = (this.lastVolume === 0); 
        this.isMobile = window.innerWidth <= 768; 
        this.isDraggingProgress = false;
        this.wasPlayingBeforeDrag = false; 

        this.beatsPerLoad = parseInt(document.getElementById('beats-per-load-config')?.dataset.beatsPerLoad || 50);
        this.currentlyDisplayedBeats = 0;
        
        this.allBeats = [];
        this.filteredBeats = [];
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
        this.loadAllBtn = null;

        this.currentSort = { column: 'date', direction: 'desc' };

        // autoplay + keyboard shortcuts
        this.autoplayEnabled = true;
        this.seekStep = 5;
        this.volumeSliderTimeout = null;

        this.init();

        this.initVolumeControls();
        this.initKeyboardControls();
        this.initScrollVolumeControl(); 
        this.currentPage = Number(window.beatStoreConfig?.currentPage || 1);
        this.totalPages = Number(window.beatStoreConfig?.totalPages || 1);
        this.isFetchingNextPage = false;
        this.nextCursor = null;
        this.hasNextPage = null;
        this.searchEndpoint = window.beatStoreConfig?.beatSearchEndpoint || '/apps/beat-search';
        this.searchDebounceTimer = null;
        this.activeRequestId = 0;
        this.initLoadMoreSystem();
        this.updateLoadMoreButton?.();

        const shouldReplaceInitialDom = this.hasActiveServerQuery();
        this.fetchServerBeats({
            append: false,
            preserveScroll: shouldReplaceInitialDom,
            silentError: true,
            closePlayerOnReplace: false,
            replaceOnSuccess: shouldReplaceInitialDom
        });

        window.addEventListener('popstate', () => {
            this.hydrateUiFromUrl();
            this.fetchServerBeats({ append: false, preserveScroll: true, silentError: true, closePlayerOnReplace: false });
        });
    }

    getRootUrl() {
        let root = window.beatStoreConfig?.rootUrl || '/';

        if (!root.startsWith('/')) {
            root = `/${root}`;
        }

        if (!root.endsWith('/')) {
            root = `${root}/`;
        }

        return root;
    }

    buildLocalizedUrl(path = '') {
        const root = this.getRootUrl();
        const cleanPath = String(path).replace(/^\/+/, '');
        return `${root}${cleanPath}`;
    }

    isNewTabIntent(event) {
        return (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        );
    }

    bindProductNavigation(beatItem) {
        if (!beatItem || beatItem.dataset.productNavigationBound === 'true') return;

        beatItem.dataset.productNavigationBound = 'true';

        const rowLink = beatItem.querySelector('.beat-row-link');

        if (rowLink) {
            rowLink.addEventListener('click', (e) => {
                if (this.isNewTabIntent(e)) return;

                e.preventDefault();
                e.stopPropagation();

                this.playBeat(beatItem);
            });
        }

        const cartBtn = beatItem.querySelector('.beat-cart-btn');

        if (cartBtn) {
            cartBtn.addEventListener('click', (e) => {
                e.stopPropagation();

                if (cartBtn.matches('a[href]')) return;

                e.preventDefault();

                const productUrl =
                    beatItem.dataset.productUrl ||
                    (beatItem.dataset.productId
                        ? this.buildLocalizedUrl(`products/${beatItem.dataset.productId}`)
                        : '');

                if (productUrl) {
                    window.location.href = productUrl;
                } else {
                    console.error('Product list: Product URL not available for redirection.');
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }
    }

    isMobileDevice() {
        return window.innerWidth <= 768;
    }

    isAppleDevice() {
        return (
            document.documentElement.classList.contains('is-apple-device') ||
            document.body.classList.contains('is-apple-device')
        );
    }

    getCorrectDisplayValue() {
        return this.isMobileDevice() ? 'flex' : 'grid';
    }

    getStoredVolume() {
        try {
            const storedVolume = window.localStorage.getItem('beat_store_player_volume');
            if (storedVolume === null || storedVolume === '') return 50;

            const parsedVolume = parseInt(storedVolume, 10);
            if (Number.isNaN(parsedVolume)) return 50;

            return Math.max(0, Math.min(100, parsedVolume));
        } catch (error) {
            return 50;
        }
    }

    persistVolume(volume) {
        try {
            const safeVolume = Math.max(0, Math.min(100, Math.round(volume)));
            window.localStorage.setItem('beat_store_player_volume', String(safeVolume));
        } catch (error) {
            // localStorage may be unavailable in some contexts
        }
    }


init() {
    this.mountPlayerToBody();
    this.setupEventListeners();
    this.setupFilters();
    this.setupSearch();
    this.setupAnimations();
    this.setupSearchClear();
    this.initProductPreview();
    this.hydrateUiFromUrl();
    this.updatePlayPauseButton(false);
}

    mountPlayerToBody() {
        if (!this.playerBar || this.playerBar.parentElement === document.body) return;

        document.body.appendChild(this.playerBar);
        document.body.classList.add('has-global-beat-player');
    }

    setupResponsiveListeners() {
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                
                this.applyFilters();
            }, 250);
        });
    }

hasActiveServerQuery() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const q = params.get('q');
    if (q && q.trim() !== '') return true;

    const genreParam = document.querySelector('[data-filter="genre"]')?.dataset.paramName;
    if (genreParam && params.has(genreParam)) return true;

    const subParam = document.querySelector('[data-filter="subgenre"]')?.dataset.paramName;
    if (subParam && params.has(subParam)) return true;

    const bpmParam =
        document.querySelector('.bpm-range-wrapper')?.dataset.paramName ||
        'filter.p.m.custom.bpm_bucket_4';

    if (params.has(bpmParam) || params.has('bpm_min') || params.has('bpm_max')) {
        return true;
    }

    return false;
}

initLoadMoreSystem() {
    this.allBeats = Array.from(document.querySelectorAll('.beat-item'));
    this.filteredBeats = [...this.allBeats];

    this.sortAndDisplayAllBeats();
}

    async loadRemainingBeatsViaAjax() {
        const collectionHandle = window.beatStoreConfig.collectionHandle;
        const totalProducts = window.beatStoreConfig.totalProducts;
        const productsPerPage = 250;
        const totalPages = Math.ceil(totalProducts / productsPerPage);

        for (let page = 2; page <= totalPages; page++) {
            try {
                const response = await fetch(this.buildLocalizedUrl(`collections/${collectionHandle}?page=${page}`));
                const html = await response.text();

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newBeats = Array.from(doc.querySelectorAll('.beat-item'));
                
                if (newBeats.length > 0) {
                    
                    const beatList = document.querySelector('.beat-list');
                    newBeats.forEach(beat => {
                        this.allBeats.push(beat);
                        beatList.appendChild(beat);

                        this.attachBeatEventListeners(beat);
                    });
                }
                
            } catch (error) {
                console.error(`❌ Error loading page ${page}:`, error);
            }
        }
    }


sortAndDisplayAllBeats() {
    const beatList = document.querySelector('.beat-list');

    if (beatList && this.allBeats.length > 0) {
        this.allBeats.sort((a, b) => {
            let dateA = parseInt(a.dataset.createdDate, 10);
            let dateB = parseInt(b.dataset.createdDate, 10);

            if (!dateA || Number.isNaN(dateA)) dateA = 0;
            if (!dateB || Number.isNaN(dateB)) dateB = 0;

            return dateB - dateA;
        });

        beatList.innerHTML = '';
        this.allBeats.forEach((item) => {
            beatList.appendChild(item);
        });
    }

    this.filteredBeats = [...this.allBeats];
    this.currentlyDisplayedBeats = this.filteredBeats.length;

    this.loadMoreContainer = document.querySelector('.beat-load-more-container');
    this.loadMoreBtn = document.querySelector('.beat-load-more-btn');
    this.loadAllBtn = document.querySelector('.beat-load-all-btn');

    if (this.loadMoreBtn && !this.loadMoreBtn.dataset.beatLoadMoreBound) {
        this.loadMoreBtn.dataset.beatLoadMoreBound = 'true';
        this.loadMoreBtn.addEventListener('click', () => this.loadMoreBeats());
    }

    if (this.loadAllBtn && !this.loadAllBtn.dataset.beatLoadAllBound) {
        this.loadAllBtn.dataset.beatLoadAllBound = 'true';
        this.loadAllBtn.addEventListener('click', () => this.loadAllBeats());
    }

    this.updateVisibleBeats();

    requestAnimationFrame(() => {
        this.setupColumnSort();
    });
}


updateVisibleBeats() {
    this.filteredBeats.forEach((beat) => {
        beat.classList.remove('beat-hidden-by-loadmore');
    });

    this.currentlyDisplayedBeats = this.filteredBeats.length;

    this.updateLoadMoreButton();
    this.triggerBeatAnimations();
}


async loadMoreBeats() {
    if (!this.loadMoreBtn) return;
    if (this.isFetchingNextPage || !this.hasNextPage) {
        this.updateLoadMoreButton();
        return;
    }

    this.loadMoreBtn.classList.add('loading');

    try {
        await this.fetchServerBeats({ append: true });
    } finally {
        this.loadMoreBtn.classList.remove('loading');
    }
}


async fetchNextPage() {
    return this.fetchServerBeats({ append: true });
}


async loadAllBeats() {
    if (!this.loadAllBtn) return;

    this.loadAllBtn.classList.add('loading');

    try {
        while (this.hasNextPage) {
            await this.fetchServerBeats({ append: true });
        }
    } finally {
        this.loadAllBtn.classList.remove('loading');
    }
}


setLoadMoreState(state) {
    if (!this.loadMoreContainer) return;

    this.loadMoreContainer.dataset.loadMoreState = state;

    if (state === 'visible' || state === 'loading') {
        this.loadMoreContainer.hidden = false;
        this.loadMoreContainer.classList.add('is-visible');

        this.loadMoreContainer.style.setProperty('display', 'flex', 'important');
        this.loadMoreContainer.style.setProperty('opacity', '1', 'important');
        this.loadMoreContainer.style.setProperty('visibility', 'visible', 'important');
        this.loadMoreContainer.style.setProperty('pointer-events', 'auto', 'important');
        this.loadMoreContainer.style.setProperty('transform', 'none', 'important');

        if (this.loadMoreBtn) {
            this.loadMoreBtn.setAttribute('aria-hidden', 'false');
            this.loadMoreBtn.disabled = state === 'loading';
        }

        return;
    }

    this.loadMoreContainer.classList.remove('is-visible');
    this.loadMoreContainer.hidden = true;

    this.loadMoreContainer.style.removeProperty('display');
    this.loadMoreContainer.style.removeProperty('opacity');
    this.loadMoreContainer.style.removeProperty('visibility');
    this.loadMoreContainer.style.removeProperty('pointer-events');
    this.loadMoreContainer.style.removeProperty('transform');

    if (this.loadMoreBtn) {
        this.loadMoreBtn.setAttribute('aria-hidden', 'true');
        this.loadMoreBtn.disabled = true;
    }
}

updateLoadMoreButton() {
    if (!this.loadMoreContainer) return;

    if (this.isFetchingNextPage && this.hasNextPage === true) {
        this.setLoadMoreState('loading');
        return;
    }

    if (this.hasNextPage === true && this.nextCursor) {
        this.setLoadMoreState('visible');
        return;
    }

    this.setLoadMoreState('hidden');
}

    triggerBeatAnimations() {
        const beatList = document.querySelector('.beat-list');

        if (beatList) {
            beatList.classList.add('is-visible');
        }

        this.filteredBeats.forEach((beat) => {
            beat.classList.add('is-visible');
        });
    }

    setupProductLicenseNoteAnimation() {
        const picker = document.querySelector('[data-license-picker]');
        const note = picker?.querySelector('[data-license-selection-note]');

        if (!picker || !note || note.dataset.licenseNoteAnimationBound === 'true') return;

        note.dataset.licenseNoteAnimationBound = 'true';

        const animateNoteChange = () => {
            note.classList.add('is-changing');

            window.setTimeout(() => {
                note.classList.remove('is-changing');
            }, 140);
        };

        picker.addEventListener('change', (event) => {
            if (!event.target.matches('input, select')) return;

            animateNoteChange();
        }, true);
    }
    
    setupAnimations() {
        const productRevealElements = Array.from(document.querySelectorAll('.vodka-product-reveal'));

        if (productRevealElements.length) {
            productRevealElements.forEach((el, index) => {
                el.style.transitionDelay = `${Math.min(index * 35, 70)}ms`;
            });

            window.requestAnimationFrame(() => {
                productRevealElements.forEach((el) => {
                    el.classList.add('is-visible');
                });
            });
        }

        const animatedElements = [
            document.querySelector('.beat-store-heading'),
            document.querySelector('.beat-collection-description'),
            document.querySelector('.beat-filters-bar'),
            document.querySelector('.beat-search-container'),
            document.querySelector('.beat-list-header'),
            document.querySelector('.beat-list'),
        ].filter(Boolean);

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    obs.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: '0px 0px -20px 0px',
            threshold: 0.05
        });

        animatedElements.forEach((el, index) => {
            el.style.transitionDelay = `${index * 35}ms`;
            observer.observe(el);
        });
    }

    attachBeatEventListeners(beatItem) {
        
        this.bindProductNavigation(beatItem);

        beatItem.addEventListener('click', (e) => {
            const ignoredElement = e.target.closest('.beat-actions button, .beat-actions a, .beat-row-link');
            if (ignoredElement) {
                return;
            }
            this.playBeat(beatItem);
        });

        

        const downloadBtn = beatItem.querySelector('.beat-download');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleDownload(beatItem);
            });
        }

        const shareBtn = beatItem.querySelector('.beat-share');
        if (shareBtn) {
            shareBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const shareUrl = shareBtn.dataset.url;
                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: document.title,
                            url: shareUrl
                        });
                    } catch (error) {
                        console.error('Share failed:', error);
                    }
                } else {
                    
                    navigator.clipboard.writeText(shareUrl).then(() => {
                        this.showNotification('Lien copié dans le presse-papier!', 'success');
                    });
                }
            });
        }
    }

    setupEventListeners() {
        document.querySelectorAll('.beat-item').forEach(item => {
            this.bindProductNavigation(item);

            item.addEventListener('click', (e) => {
                const ignoredElement = e.target.closest('.beat-actions button, .beat-actions a, .beat-row-link');
                if (ignoredElement) {
                    return;
                }

                this.playBeat(item);
            });
        });

        this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
        document.getElementById('playerPrev').addEventListener('click', () => this.playPrevious());
        document.getElementById('playerNext').addEventListener('click', () => this.playNext());
        document.getElementById('playerClose').addEventListener('click', () => this.closePlayer());

        // Setup progress bar drag functionality
        this.setupProgressBarDrag();


        if (this.playerCartBtn) {
            this.playerCartBtn.addEventListener('click', (e) => {
                e.stopPropagation();

                if (this.playerCartBtn.matches('a[href]')) {
                    const href = this.playerCartBtn.getAttribute('href');

                    if (href && href !== '#') {
                        return;
                    }

                    e.preventDefault();
                }

                const productUrl =
                    this.currentProductData?.productUrl ||
                    (this.currentProductData?.productHandle
                        ? this.buildLocalizedUrl(`products/${this.currentProductData.productHandle}`)
                        : '');

                if (productUrl) {
                    window.location.href = productUrl;
                } else {
                    console.error('Player: Product data or URL not available for redirection.');
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }

        document.querySelectorAll('.beat-download').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const beatItem = e.target.closest('.beat-item');
                if (beatItem) {
                    this.handleDownload(beatItem);
                }
            });
        });

        document.querySelectorAll('.beat-share').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const beatItem = e.target.closest('.beat-item');
                if (beatItem) {
                    const shareUrl = btn.dataset.url;
                    if (navigator.share) {
                        try {
                            await navigator.share({
                                title: document.title,
                                url: shareUrl
                            });
                        } catch (error) {
                            console.error('Error sharing:', error);
                        }
                    } else {
                        try {
                            await navigator.clipboard.writeText(shareUrl);
                            alert('Lien copié dans le presse-papiers !');
                        } catch (err) {
                            console.error('Impossible de copier le lien: ', err);
                            prompt('Copiez ce lien manuellement:', shareUrl);
                        }
                    }
                }
            });
        });
    }


    setupFilterSelectHoverRelease() {
        const selects = document.querySelectorAll('.beat-filters-panel .beat-filter-select');
        if (!selects.length) return;

        selects.forEach((select) => {
            if (select.dataset.hoverReleaseBound === 'true') return;
            select.dataset.hoverReleaseBound = 'true';

            let focusedAt = 0;

            const releaseHover = () => {
                select.blur();
                select.classList.add('beat-filter-select--release-hover');
            };

            const restoreHover = () => {
                select.classList.remove('beat-filter-select--release-hover');
            };

            select.addEventListener('focus', () => {
                focusedAt = Date.now();
                restoreHover();
            });

            select.addEventListener('pointerleave', restoreHover);

            select.addEventListener('change', () => {
                window.setTimeout(releaseHover, 0);
            });

            select.addEventListener('keydown', (event) => {
                if (event.key === 'Escape' || event.key === 'Enter') {
                    window.setTimeout(releaseHover, 0);
                }
            });

            /*
            select.addEventListener('click', () => {
                const wasAlreadyFocused = Date.now() - focusedAt > 180;

                if (document.activeElement === select && wasAlreadyFocused) {
                    window.setTimeout(releaseHover, 0);
                }
            });
            */
        });
    }

    setupFilters() {
        this.setupFilterDrawerUI();
        this.setupFilterSelectHoverRelease();

        const applyBtn = document.querySelector('.beat-filters-apply');
        const resetBtn = document.querySelector('.beat-filters-reset');

        applyBtn?.addEventListener('click', () => {
            this.closeFiltersDrawer?.();
            this.applyServerFilters();
        });

        resetBtn?.addEventListener('click', () => {
            this.closeFiltersDrawer?.();
            this.resetServerFilters();
        });

        document.querySelectorAll('.beat-bpm-min, .beat-bpm-max').forEach((el) => {
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    el.blur();
                }
            });
        });
    }

    setupFilterDrawerUI() {
        const toggle = document.querySelector('.beat-filters-toggle');
        const drawer = document.querySelector('.beat-filters-drawer');
        const panel  = document.querySelector('.beat-filters-panel'); // ✅ peut être dans body
        const summary = document.getElementById('BeatFiltersSummary');
        if (!toggle || !drawer || !panel) return;

        // --- Portaling (sortir du stacking context) ---
        const panelHome = panel.parentNode;
        const panelPlaceholder = document.createComment('beat-filters-panel-placeholder');

        const mountPanelToBody = () => {
            if (panel.parentNode === document.body) return;
            if (panelHome) panelHome.replaceChild(panelPlaceholder, panel);
            document.body.appendChild(panel);
        };

        const restorePanelHome = () => {
            if (panelPlaceholder.parentNode) {
                panelPlaceholder.parentNode.replaceChild(panel, panelPlaceholder);
            }
        };

        // --- Header bottom (pour rester sous le header sticky/fixed) ---
        const getHeaderBottom = () => {
            let bottom = 0;
            const stack = document.elementsFromPoint(window.innerWidth / 2, 1);
            stack.forEach((el) => {
                if (!el || el === document.documentElement || el === document.body) return;
                const cs = getComputedStyle(el);
                const top = parseFloat(cs.top || '0');
                if ((cs.position === 'fixed' || cs.position === 'sticky') && top <= 2) {
                    const r = el.getBoundingClientRect();
                    if (r.height > 0) bottom = Math.max(bottom, r.bottom);
                }
            });
            return bottom;
        };

        

        const positionPanel = () => {
            const toggleRect = toggle.getBoundingClientRect();

            const searchEl = document.querySelector('.beat-search-container');
            const rectSource = searchEl ? searchEl.getBoundingClientRect() : toggleRect;

            const viewportPadding = 12;
            const margin = 0;

            let left = rectSource.left;
            let width = rectSource.width;

            if (left < viewportPadding) left = viewportPadding;
            if (left + width > window.innerWidth - viewportPadding) {
                width = window.innerWidth - viewportPadding * 2;
                left = viewportPadding;
            }

            

            panel.style.position = 'absolute';
            panel.style.left = `${window.scrollX + left}px`;
            panel.style.top = `${window.scrollY + toggleRect.bottom + margin}px`;
            panel.style.width = `${width}px`;
            panel.style.zIndex = '900';
        };

        const icon = toggle.querySelector('.beat-filters-toggle-icon');

        const open = () => {
            mountPanelToBody();
            positionPanel();
            panel.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            toggle.setAttribute('aria-expanded', 'true');
            if (icon) icon.textContent = '▴';
        };

        const close = () => {
            panel.classList.remove('is-open');
            drawer.setAttribute('aria-hidden', 'true');
            toggle.setAttribute('aria-expanded', 'false');
            if (icon) icon.textContent = '▾';

            // remet le panel après l'anim
            window.setTimeout(() => {
                if (!panel.classList.contains('is-open')) restorePanelHome();
            }, 260);
        };

        this.openFiltersDrawer = open;
        this.closeFiltersDrawer = close;

        const closeFiltersWhenMenuDrawerOpens = (event) => {
            if (!panel.classList.contains('is-open')) return;

            if (
                event.target.closest(
                    '.beat-download, .beat-cart-btn, .show-license, #cart-icon-bubble, .header__icon--cart, header-drawer summary, .header__icon--menu'
                )
            ) {
                close();
            }
        };

        document.addEventListener('pointerdown', closeFiltersWhenMenuDrawerOpens, true);

        document.querySelectorAll('header-drawer details').forEach((menuDetails) => {
            const menuDrawerObserver = new MutationObserver(() => {
                if (
                    panel.classList.contains('is-open') &&
                    menuDetails.hasAttribute('open')
                ) {
                    close();
                }
            });

            menuDrawerObserver.observe(menuDetails, {
                attributes: true,
                attributeFilter: ['open', 'class']
            });
        });

        const closeFiltersBeforeHigherOverlay = (event) => {
            if (!panel.classList.contains('is-open')) return;

            if (
                event.target.closest(
                    '.beat-download, .beat-cart-btn, .show-license, #cart-icon-bubble, .header__icon--cart'
                )
            ) {
                close();
            }
        };

        document.addEventListener('pointerdown', closeFiltersBeforeHigherOverlay, true);

        const isVisibleOverlay = (el) => {
            if (!el) return false;

            const style = window.getComputedStyle(el);

            return (
                style.display !== 'none' &&
                style.visibility !== 'hidden'
            );
        };

        const closeFiltersIfHigherOverlayIsOpen = () => {
            if (!panel.classList.contains('is-open')) return;

            const cartDrawer = document.querySelector('cart-drawer');
            const cartIsOpen =
                cartDrawer?.classList.contains('active') ||
                cartDrawer?.querySelector('.drawer.active');

            const pricingLicenseModal = document.getElementById('license-modal');
            const beatLicenseModal = document.getElementById('licenseModal');
            const freeDownloadPopup = document.getElementById('free-download-popup');

            if (
                cartIsOpen ||
                isVisibleOverlay(pricingLicenseModal) ||
                isVisibleOverlay(beatLicenseModal) ||
                isVisibleOverlay(freeDownloadPopup)
            ) {
                close();
            }
        };

        const overlayObserver = new MutationObserver(closeFiltersIfHigherOverlayIsOpen);

        document.querySelectorAll('cart-drawer, #license-modal, #licenseModal').forEach((el) => {
            overlayObserver.observe(el, {
                attributes: true,
                attributeFilter: ['class', 'style']
            });
        });

        overlayObserver.observe(document.body, {
            childList: true
        });

        toggle.addEventListener('pointerleave', () => {
            toggle.classList.remove('beat-filters-toggle--release-hover');
        });

        toggle.addEventListener('focus', () => {
            toggle.classList.remove('beat-filters-toggle--release-hover');
        });

        toggle.addEventListener('click', () => {
            if (panel.classList.contains('is-open')) {
                close();

                toggle.blur();
                toggle.classList.add('beat-filters-toggle--release-hover');

                return;
            }

            toggle.classList.remove('beat-filters-toggle--release-hover');
            open();
        });

        // Click extérieur => close (desktop uniquement)
        const onOutsideClick = (e) => {
            if (!panel.classList.contains('is-open')) return;
            if (window.innerWidth < 800) return;

            const clickedInsidePanel = panel.contains(e.target);
            const clickedToggle = toggle.contains(e.target);
            if (!clickedInsidePanel && !clickedToggle) close();
        };
        document.addEventListener('pointerdown', onOutsideClick, true);

        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

        // Reposition si scroll/resize / clavier mobile
        window.addEventListener('resize', () => { if (panel.classList.contains('is-open')) positionPanel(); });
        window.addEventListener('scroll',  () => { if (panel.classList.contains('is-open')) positionPanel(); }, { passive: true });

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                if (panel.classList.contains('is-open')) positionPanel();
            });
            window.visualViewport.addEventListener('scroll', () => {
                if (panel.classList.contains('is-open')) positionPanel();
            });
        }

        this.renderFiltersSummary(summary);

        summary?.addEventListener('click', (e) => {
            const btn = e.target.closest('.beat-filter-chip-remove');
            if (!btn) return;

            const param = btn.dataset.removeParam;
            if (!param) return;

            const url = new URL(window.location.href);
            url.searchParams.delete('page');
            url.searchParams.delete('sort_by');
            url.searchParams.delete(param);

            const bpmWrapper = document.querySelector('.bpm-range-wrapper');
            const bpmParam = bpmWrapper?.dataset.paramName || 'filter.p.m.custom.bpm_bucket_4';

            if (param === bpmParam) {
                url.searchParams.delete('bpm_min');
                url.searchParams.delete('bpm_max');

                const minEl = document.querySelector('.beat-bpm-min');
                const maxEl = document.querySelector('.beat-bpm-max');
                if (minEl) minEl.value = '';
                if (maxEl) maxEl.value = '';
            }

            const nextUrl = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
            window.history.replaceState({}, '', nextUrl);
            this.hydrateUiFromUrl();
            this.fetchServerBeats({ append: false, preserveScroll: true });
        });
    }

    renderFiltersSummary(container) {
        if (!container) return;
        container.innerHTML = '';

        const removeFilterAriaLabel =
            container.dataset.removeFilterAriaLabel || 'Remove filter';

        const bpmRangeTemplate =
            container.dataset.bpmRangeTemplate || '__MIN__-__MAX__ BPM';

        const bpmGreaterThanTemplate =
            container.dataset.bpmGreaterThanTemplate || 'BPM > __VALUE__';

        const bpmLessThanTemplate =
            container.dataset.bpmLessThanTemplate || 'BPM < __VALUE__';

        const applyTemplate = (template, replacements = {}) => {
            let output = template || '';

            Object.entries(replacements).forEach(([key, value]) => {
                output = output.replaceAll(`__${key.toUpperCase()}__`, String(value));
            });

            return output;
        };

        const makeChip = (label, paramName) => {
            const chip = document.createElement('span');
            chip.className = 'beat-filter-chip';

            const text = document.createElement('span');
            text.className = 'beat-filter-chip-label';
            text.textContent = label;

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'beat-filter-chip-remove';
            remove.textContent = '×';
            remove.setAttribute('aria-label', removeFilterAriaLabel);
            remove.dataset.removeParam = paramName;

            chip.appendChild(text);
            chip.appendChild(remove);
            return chip;
        };

        // Genre
        const genreEl = document.querySelector('[data-filter="genre"]');
        const genreParam = genreEl?.dataset.paramName;
        if (genreEl && genreParam && genreEl.value && genreEl.value !== 'all') {
            const label = genreEl.options[genreEl.selectedIndex]?.textContent?.trim() || genreEl.value;
            container.appendChild(makeChip(label, genreParam));
        }

        // Sous-genre
        const subEl = document.querySelector('[data-filter="subgenre"]');
        const subParam = subEl?.dataset.paramName;
        if (subEl && subParam && subEl.value && subEl.value !== 'all') {
            const label = subEl.options[subEl.selectedIndex]?.textContent?.trim() || subEl.value;
            container.appendChild(makeChip(label, subParam));
        }

        // BPM (buckets répétés)
        const bpmWrapper = document.querySelector('.bpm-range-wrapper');
        const bpmParam = bpmWrapper?.dataset.paramName || 'filter.p.m.custom.bpm_bucket_4';
        const params = new URL(window.location.href).searchParams;

        const rawMin = params.get('bpm_min') || '';
        const rawMax = params.get('bpm_max') || '';

        if (rawMin || rawMax) {
            let label = '';

            if (rawMin && rawMax) {
                label = applyTemplate(bpmRangeTemplate, {
                    min: rawMin,
                    max: rawMax
                });
            } else if (rawMin) {
                label = applyTemplate(bpmGreaterThanTemplate, {
                    value: rawMin
                });
            } else {
                label = applyTemplate(bpmLessThanTemplate, {
                    value: rawMax
                });
            }

            container.appendChild(makeChip(label, bpmParam));
        }
    }


resetServerFilters() {
    document.querySelectorAll('.beat-filter-select').forEach((sel) => {
        sel.value = 'all';
    });

    const minEl = document.querySelector('.beat-bpm-min');
    const maxEl = document.querySelector('.beat-bpm-max');
    if (minEl) minEl.value = '';
    if (maxEl) maxEl.value = '';

    const searchInput = document.querySelector('.beat-search-input');
    if (searchInput) {
        searchInput.value = '';
        searchInput.blur();
    }

    this.updateSearchClearVisibility();
    this.syncUrlFromUi();
    this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
    this.fetchServerBeats({ append: false, preserveScroll: true });
}

hydrateUiFromUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const searchInput = document.querySelector('.beat-search-input');
    if (searchInput) {
        searchInput.value = params.get('q') || '';
    }
    this.updateSearchClearVisibility();

    const genreEl = document.querySelector('[data-filter="genre"]');
    const genreParam = genreEl?.dataset.paramName;
    if (genreEl && genreParam) {
        genreEl.value = params.get(genreParam) || 'all';
    }

    const subEl = document.querySelector('[data-filter="subgenre"]');
    const subParam = subEl?.dataset.paramName;
    if (subEl && subParam) {
        subEl.value = params.get(subParam) || 'all';
    }

    const bpmWrapper = document.querySelector('.bpm-range-wrapper');
    const bpmParam = bpmWrapper?.dataset.paramName || 'filter.p.m.custom.bpm_bucket_4';
    const bpmValues = params.getAll(bpmParam);

    const rawMin = params.get('bpm_min') || '';
    const rawMax = params.get('bpm_max') || '';

    const minEl = document.querySelector('.beat-bpm-min');
    const maxEl = document.querySelector('.beat-bpm-max');

    if (rawMin || rawMax) {
        if (minEl) minEl.value = rawMin;
        if (maxEl) maxEl.value = rawMax;
    } else if (bpmValues.length) {
        let min = Infinity;
        let max = -Infinity;

        bpmValues.forEach((value) => {
            const match = /^(\d+)-(\d+)$/.exec(value);
            if (!match) return;
            min = Math.min(min, Number(match[1]));
            max = Math.max(max, Number(match[2]));
        });

        if (minEl) minEl.value = Number.isFinite(min) ? String(min) : '';
        if (maxEl) maxEl.value = Number.isFinite(max) ? String(max) : '';
    } else {
        if (minEl) minEl.value = '';
        if (maxEl) maxEl.value = '';
    }

    this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
}

syncUrlFromUi() {
    const url = new URL(window.location.href);

    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');

    const searchInput = document.querySelector('.beat-search-input');
    const q = searchInput?.value?.trim() || '';
    if (q) {
        url.searchParams.set('q', q);
    } else {
        url.searchParams.delete('q');
    }

    const genreEl = document.querySelector('[data-filter="genre"]');
    const genreParam = genreEl?.dataset.paramName;
    if (genreEl && genreParam) {
        url.searchParams.delete(genreParam);
        if (genreEl.value && genreEl.value !== 'all') {
            url.searchParams.set(genreParam, genreEl.value);
        }
    }

    const subEl = document.querySelector('[data-filter="subgenre"]');
    const subParam = subEl?.dataset.paramName;
    if (subEl && subParam) {
        url.searchParams.delete(subParam);
        if (subEl.value && subEl.value !== 'all') {
            url.searchParams.set(subParam, subEl.value);
        }
    }

    const bpmWrapper = document.querySelector('.bpm-range-wrapper');
        const bpmParam = bpmWrapper?.dataset.paramName || 'filter.p.m.custom.bpm_bucket_4';

        url.searchParams.delete(bpmParam);
        url.searchParams.delete('bpm_min');
        url.searchParams.delete('bpm_max');

        const minStr = document.querySelector('.beat-bpm-min')?.value?.trim() || '';
        const maxStr = document.querySelector('.beat-bpm-max')?.value?.trim() || '';

        const hasMin = minStr !== '' && !Number.isNaN(Number(minStr));
        const hasMax = maxStr !== '' && !Number.isNaN(Number(maxStr));

        if (hasMin) {
            url.searchParams.set('bpm_min', minStr);
        }

        if (hasMax) {
            url.searchParams.set('bpm_max', maxStr);
    }

    const nextUrl = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
    window.history.replaceState({}, '', nextUrl);
    return url;
}

buildBeatSearchRequestUrl(after = null) {
    const endpoint = this.searchEndpoint || '/apps/beat-search';
    const url = new URL(endpoint, window.location.origin);
    const pageUrl = new URL(window.location.href);

    const bpmWrapper = document.querySelector('.bpm-range-wrapper');
    const bpmParam = bpmWrapper?.dataset.paramName || 'filter.p.m.custom.bpm_bucket_4';

    pageUrl.searchParams.forEach((value, key) => {
        if (
            key === 'page' ||
            key === 'sort_by' ||
            key === bpmParam
        ) return;

        url.searchParams.append(key, value);
    });

    if (window.beatStoreConfig?.collectionHandle) {
        url.searchParams.set('collection_handle', window.beatStoreConfig.collectionHandle);
    }

    if (window.beatStoreConfig?.collectionId) {
        url.searchParams.set('collection_id', String(window.beatStoreConfig.collectionId));
    }

    url.searchParams.set('limit', String(this.beatsPerLoad));

    if (after) {
        url.searchParams.set('after', after);
    }

    return url.toString();
}

async fetchServerBeats({
    append = false,
    preserveScroll = false,
    silentError = false,
    closePlayerOnReplace = true,
    replaceOnSuccess = true
} = {}) {
    if (!this.searchEndpoint) return;
    if (this.isFetchingNextPage) return;

    const beatList = document.querySelector('.beat-list');
    const requestId = ++this.activeRequestId;
    const previousScrollY = window.scrollY;

    const shouldTouchDom = append || replaceOnSuccess;

    this.isFetchingNextPage = true;
    this.updateLoadMoreButton();

    if (beatList && shouldTouchDom) {
        beatList.classList.add('is-loading');
    }

    try {
        const response = await fetch(
            this.buildBeatSearchRequestUrl(append ? this.nextCursor : null),
            {
                headers: {
                    Accept: 'application/json'
                }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();

        if (requestId !== this.activeRequestId) {
            return;
        }

        const items = payload.items || payload.beats || payload.products || [];
        const pageInfo = payload.pageInfo || payload.pagination || {};

        const endCursor =
            pageInfo.endCursor ||
            pageInfo.nextCursor ||
            pageInfo.end_cursor ||
            pageInfo.next_cursor ||
            null;

        const hasNextPage =
            pageInfo.hasNextPage === true ||
            pageInfo.has_next_page === true ||
            pageInfo.hasNext === true;

        this.nextCursor = hasNextPage ? endCursor : null;
        this.hasNextPage = Boolean(hasNextPage && endCursor);

        if (shouldTouchDom) {
            this.renderServerBeats(items, { append, closePlayerOnReplace });
        }

        if (preserveScroll && shouldTouchDom) {
            window.scrollTo({ top: previousScrollY });
        }
    } catch (error) {
        console.error('Beat search request failed:', error);

        if (!append) {
            this.hasNextPage = false;
            this.nextCursor = null;
        }

        if (!silentError) {
            this.showNotification(
                window.beatStoreConfig?.beatSearchErrorMessage || 'La recherche est indisponible pour le moment.',
                'error'
            );
        }
    } finally {
        this.isFetchingNextPage = false;

        if (beatList && shouldTouchDom) {
            beatList.classList.remove('is-loading');
        }

        this.updateLoadMoreButton();
    }
}

renderServerBeats(items, { append = false, closePlayerOnReplace = true } = {}) {
    const beatList = document.querySelector('.beat-list');
    if (!beatList) return;

    if (!append) {
        if (this.currentAudio && closePlayerOnReplace) {
            this.closePlayer();
        }

        beatList.innerHTML = '';
        this.allBeats = [];
        this.filteredBeats = [];
    }

    if (!Array.isArray(items) || !items.length) {
        if (!append) {
            const emptyMessage = window.beatStoreConfig?.beatSearchEmptyMessage || '';

            beatList.innerHTML = `
                <div class="beat-empty-state" role="status" aria-live="polite">
                    ${this.escapeHtml(emptyMessage)}
                </div>
            `;
        }

        this.currentlyDisplayedBeats = this.filteredBeats.length;
        return;
    }


    if (append) {
        beatList.querySelector('.beat-empty-state')?.remove();
    }
    
    items.forEach((item) => {
        const beatEl = this.createBeatItemElement(item);
        beatList.appendChild(beatEl);
        this.allBeats.push(beatEl);
        this.filteredBeats.push(beatEl);
        this.attachBeatEventListeners(beatEl);
    });

    this.currentlyDisplayedBeats = this.filteredBeats.length;
    this.updateVisibleBeats();
}

createBeatItemElement(item) {
    const tags = Array.isArray(item.tags) ? item.tags.filter(Boolean) : [];
    const title = item.title || '';
    const genre = item.genre || item.genre || '';
    const subgenre = item.subgenre || item.subGenre || item.customSubgenre || '';
    const bpm = item.bpm ?? '';
    const duration = item.duration || '0:00';
    const createdDateUnix = item.createdDateUnix || this.toUnixTimestamp(item.createdAt) || 0;
    const createdDateLabel = item.createdDateLabel || this.formatDateLabel(item.createdAt) || '';
    const imageUrl = item.imageUrl || '';
    const audioUrl = item.audioUrl || '';
    const productHandle = item.handle || '';
    const fallbackProductUrl = productHandle ? this.buildLocalizedUrl(`products/${productHandle}`) : '#';
    const shareUrl = item.shareUrl || fallbackProductUrl;
    const productUrl = item.productUrl || item.url || shareUrl || fallbackProductUrl;
    const variantId = item.variantId || '';

    const el = document.createElement('div');
    el.className = 'beat-item';
    el.dataset.productId = productHandle;
    el.dataset.productUrl = productUrl;
    el.dataset.variantId = variantId;
    el.dataset.category = item.productType || '';
    el.dataset.bpm = String(bpm || '');
    el.dataset.price = item.priceFormatted || '';
    el.dataset.tags = JSON.stringify(tags);
    el.dataset.img = imageUrl;
    el.dataset.title = title;
    el.dataset.createdDate = String(createdDateUnix || 0);
    el.dataset.durationSeconds = String(item.durationSeconds || this.parseDurationToSeconds(duration));

    const tagsHtml = tags.length
        ? tags.slice(0, 8).map((tag) => `<span class="beat-tag">${this.escapeHtml(tag)}</span>`).join('')
        : '&nbsp;';

    const coverHtml = imageUrl
        ? `<img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(title)}" loading="lazy">`
        : '<div class="beat-cover-placeholder"></div>';

    const audioHtml = audioUrl
        ? `<audio class="beat-audio" preload="none" data-src="${this.escapeAttribute(audioUrl)}"><source src="${this.escapeAttribute(audioUrl)}" type="audio/mpeg"></audio>`
        : '';

    const subtitleHtml = (genre || subgenre)
    ? `<div class="beat-subtitle">
        ${genre ? `<span class="beat-genre">${this.escapeHtml(String(genre))}</span>` : ''}
        ${genre && subgenre ? `<span class="beat-subtitle-separator"> · </span>` : ''}
        ${subgenre ? `<span class="beat-subgenre">${this.escapeHtml(String(subgenre))}</span>` : ''}
        </div>`
    : '';

    const downloadHtml = window.beatStoreConfig?.showDownloadButton
        ? `<button class="beat-download" aria-label="${this.escapeAttribute(window.beatStoreConfig?.downloadAriaLabel || 'Download beat')}" data-product-handle="${this.escapeAttribute(productHandle)}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 1v12m0 0l4-4m-4 4l-4-4M1 14v4a1 1 0 001 1h16a1 1 0 001-1v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>`
        : '';

    const shareHtml = window.beatStoreConfig?.showShareButton
        ? `<button class="beat-share" aria-label="${this.escapeAttribute(window.beatStoreConfig?.shareAriaLabel || 'Share beat')}" data-url="${this.escapeAttribute(shareUrl)}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="5" cy="10" r="3" stroke="currentColor" stroke-width="2"></circle>
              <circle cx="15" cy="5" r="3" stroke="currentColor" stroke-width="2"></circle>
              <circle cx="15" cy="15" r="3" stroke="currentColor" stroke-width="2"></circle>
              <path d="M7.5 8.5l5-3M7.5 11.5l5 3" stroke="currentColor" stroke-width="2"></path>
            </svg>
          </button>`
        : '';

    el.innerHTML = `
        <a
            class="beat-row-link"
            href="${this.escapeAttribute(productUrl)}"
            aria-label="Open ${this.escapeAttribute(title)}"
            tabindex="-1">
        </a>

        <div class="beat-cover">
          ${coverHtml}
        </div>

        <div class="beat-info">
        <h3 class="beat-title">${this.escapeHtml(title)}</h3>
            ${subtitleHtml}
            ${audioHtml}
        </div>

        <div class="beat-time">${this.escapeHtml(String(duration))}</div>
        <div class="beat-bpm">${this.escapeHtml(String(bpm))}</div>

        <div class="beat-tags">
          ${tagsHtml}
        </div>

        <div class="beat-actions">
          ${downloadHtml}
          ${shareHtml}
            <a
                class="beat-cart-btn"
                href="${this.escapeAttribute(productUrl)}"
                data-price="${this.escapeAttribute(item.priceFormatted || '')}">
                <img
                    src="${this.escapeAttribute(window.beatStoreConfig?.cartIconUrl || '')}"
                    alt=""
                    class="cart-icon-img"
                    width="20"
                    height="20"
                    loading="lazy"
                    aria-hidden="true"
                >
                <span>${this.escapeHtml(window.beatStoreConfig?.buyButtonText || 'Buy')}</span>
            </a>
        </div>
    `;

    return el;
}

parseDurationToSeconds(duration) {
    if (!duration || typeof duration !== 'string') return 0;

    const parts = duration.split(':').map((part) => Number(part));
    if (parts.some((value) => Number.isNaN(value))) return 0;

    if (parts.length === 2) {
        return parts[0] * 60 + parts[1];
    }

    if (parts.length === 3) {
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    return 0;
}

toUnixTimestamp(dateValue) {
    if (!dateValue) return 0;
    const parsed = Date.parse(dateValue);
    if (Number.isNaN(parsed)) return 0;
    return Math.floor(parsed / 1000);
}

formatDateLabel(dateValue) {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    });
}

escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

escapeAttribute(value) {
    return this.escapeHtml(value);
}



applyServerFilters() {
    this.syncUrlFromUi();
    this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
    this.fetchServerBeats({ append: false, preserveScroll: true });
}

    setupProgressBarDrag() {
        if (!this.progressBar) return;

        const setProgressFromEvent = (e) => {
            if (!this.currentAudio || !this.currentAudio.duration) return;
            
            const rect = this.progressBar.getBoundingClientRect();
            const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            this.currentAudio.currentTime = percent * this.currentAudio.duration;
        };

        const startDrag = (e) => {
            // Sauvegarder l'état de lecture actuel
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            
            // Mettre en pause si en lecture
            if (this.wasPlayingBeforeDrag) {
                this.currentAudio.pause();
            }
            
            this.isDraggingProgress = true;
            setProgressFromEvent(e);
            
            // Ajouter une classe pour le style visuel pendant le drag
            this.progressBar.classList.add('dragging');
            
            // Empêcher la sélection de texte pendant le drag
            e.preventDefault();
        };

        const moveDrag = (e) => {
            if (this.isDraggingProgress) {
                setProgressFromEvent(e);
            }
        };

        const endDrag = () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                
                // Reprendre la lecture si elle était en cours avant le drag
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        };

        // Click simple pour sauter à une position
        this.progressBar.addEventListener('click', (e) => {
            if (!this.isDraggingProgress) {
                setProgressFromEvent(e);
            }
        });

        // Drag sur desktop
        this.progressBar.addEventListener('mousedown', startDrag);
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('mouseup', endDrag);

        // Support tactile pour mobile
        this.progressBar.addEventListener('touchstart', (e) => {
            // Sauvegarder l'état de lecture actuel
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            
            // Mettre en pause si en lecture
            if (this.wasPlayingBeforeDrag) {
                this.currentAudio.pause();
            }
            
            this.isDraggingProgress = true;
            this.progressBar.classList.add('dragging');
            const touch = e.touches[0];
            setProgressFromEvent(touch);
            e.preventDefault();
        }, { passive: false });

        document.addEventListener('touchmove', (e) => {
            if (this.isDraggingProgress && e.touches.length > 0) {
                const touch = e.touches[0];
                setProgressFromEvent(touch);
            }
        }, { passive: false });

        document.addEventListener('touchend', () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                
                // Reprendre la lecture si elle était en cours avant le drag
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        });
    }


setupSearch() {
    const searchInput = document.querySelector('.beat-search-input');
    const searchSubmit = document.querySelector('.beat-search-submit');

    if (!searchInput) return;

    searchInput.addEventListener('input', () => {
        this.updateSearchClearVisibility();

        window.clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = window.setTimeout(() => {
            this.syncUrlFromUi();
            this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
            this.fetchServerBeats({ append: false, preserveScroll: true });
        }, 300);
    });

    searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            searchInput.blur();
        }
    });

    if (searchSubmit) {
        searchSubmit.addEventListener('mousedown', (event) => {
            event.preventDefault();
            searchInput.blur();
        });
    }
}

setupSearchClear() {
    const searchInput = document.querySelector('.beat-search-input');
    const clearButton = document.querySelector('.beat-search-clear');

    if (!clearButton || !searchInput) return;

    clearButton.addEventListener('click', () => {
        searchInput.value = '';
        this.updateSearchClearVisibility();
        this.syncUrlFromUi();
        this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
        this.fetchServerBeats({ append: false, preserveScroll: true });
        searchInput.focus();
    });

    this.updateSearchClearVisibility();
}
    
    updateSearchClearVisibility() {
        const searchInput = document.querySelector('.beat-search-input');
        const clearButton = document.querySelector('.beat-search-clear');
        
        if (!clearButton || !searchInput) return;
        
        if (searchInput.value.length > 0) {
            clearButton.classList.add('show');
        } else {
            clearButton.classList.remove('show');
        }
    }

    setupColumnSort() {
        const headers = document.querySelectorAll('.beat-header-sortable');
        
        if (headers.length === 0) {
            
            setTimeout(() => this.setupColumnSort(), 100);
            return;
        }
        
        headers.forEach(header => {
            header.addEventListener('click', () => {
                const column = header.dataset.sort;

                if (this.currentSort.column === column) {
                    this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
                } else {
                    this.currentSort.column = column;
                    this.currentSort.direction = 'asc';
                }

                this.updateSortIcons();

                this.sortBeats();
            });
        });
    }

    updateSortIcons() {
        document.querySelectorAll('.beat-header-sortable').forEach(header => {
            const icon = header.querySelector('.sort-icon');
            if (!icon) return;
            
            if (header.dataset.sort === this.currentSort.column) {
                header.classList.add('sorted');
                if (this.currentSort.direction === 'asc') {
                    icon.style.transform = 'rotate(0deg)';
                } else {
                    icon.style.transform = 'rotate(180deg)';
                }
            } else {
                header.classList.remove('sorted');
                icon.style.transform = 'rotate(0deg)';
            }
        });
    }

    sortBeats() {
        const beatList = document.querySelector('.beat-list');
        
        this.filteredBeats.sort((a, b) => {
            let valA, valB;
            
            switch(this.currentSort.column) {
                case 'title':
                    valA = a.dataset.title.toLowerCase();
                    valB = b.dataset.title.toLowerCase();
                    return this.currentSort.direction === 'asc' 
                        ? valA.localeCompare(valB)
                        : valB.localeCompare(valA);
                
                case 'duration':
                    valA = parseInt(a.dataset.durationSeconds || 0);
                    valB = parseInt(b.dataset.durationSeconds || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
                
                case 'bpm':
                    valA = parseInt(a.dataset.bpm || 0);
                    valB = parseInt(b.dataset.bpm || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
                
                case 'date':
                default:
                    valA = parseInt(a.dataset.createdDate || 0);
                    valB = parseInt(b.dataset.createdDate || 0);
                    return this.currentSort.direction === 'asc' 
                        ? valA - valB 
                        : valB - valA;
            }
        });

        beatList.innerHTML = '';
        this.filteredBeats.forEach(beat => {
            beatList.appendChild(beat);
        });

        this.currentlyDisplayedBeats = 0;
        this.updateVisibleBeats();
    }

    

    normalizeString(str) {
        if (typeof str !== 'string') return '';
        return str
            .toLowerCase()
            .normalize("NFD") 
            .replace(/[\u0300-\u036f]/g, ""); 
    }


searchBeats(query) {
    const searchInput = document.querySelector('.beat-search-input');
    if (searchInput && typeof query === 'string') {
        searchInput.value = query;
    }

    this.updateSearchClearVisibility();
    this.syncUrlFromUi();
    this.renderFiltersSummary(document.getElementById('BeatFiltersSummary'));
    this.fetchServerBeats({ append: false, preserveScroll: true });
}

    async playBeat(beatItem) {
        const audioElement = beatItem.querySelector('.beat-audio');
        if (!audioElement) return;

        const audioUrl = audioElement.dataset.src || audioElement.querySelector('source')?.src;
        if (!audioUrl) return;

        if (this.currentBeatItem === beatItem && this.currentAudio && !this.currentAudio.paused) {
            this.togglePlayPause();
            return;
        }

        // save volume before switching tracks
        if (this.currentAudio) {
            this.lastVolume = Math.round(this.currentAudio.volume * 100);
            this.persistVolume(this.lastVolume);
            this.currentAudio.pause();
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }

        const volumeToUse = this.getStoredVolume();
        this.lastVolume = volumeToUse;

        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.preload = 'auto';
        this.currentAudio.playsInline = true;
        this.currentAudio.volume = volumeToUse / 100; 
        this.currentBeatItem = beatItem;

        this.currentAudio.addEventListener('timeupdate', () => this.updateProgress());
        this.currentAudio.addEventListener('loadedmetadata', () => this.updateTimeDisplay());
        this.currentAudio.addEventListener('ended', () => {
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
            if (this.autoplayEnabled) {
                this.playNext();
            }
        });

        this.currentAudio.addEventListener('volumechange', () => {
            if (!this.isDraggingVolume) { 
                const newVolumePercent = Math.round(this.currentAudio.volume * 100);
                this.updateVolumeIcon(newVolumePercent);
                if (this.volumeSliderFill) {
                   this.volumeSliderFill.style.height = `${newVolumePercent}%`;
                }
                if (newVolumePercent > 0) {
                    this.lastVolume = newVolumePercent;
                }
                this.persistVolume(newVolumePercent);
            }
        });

        // sync volume UI
        this.updateVolumeIcon(volumeToUse);
        if (this.volumeSliderFill) {
            this.volumeSliderFill.style.height = `${volumeToUse}%`;
        }

        this.updatePlayerUI(beatItem);
        beatItem.classList.add('playing');
        this.showPlayer();

        try {
            await this.currentAudio.play();
            this.updatePlayPauseButton(true);
        } catch (error) {
            console.error('Play error:', error);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }

    showPlayer() {
        this.playerBar.style.display = 'block';
        setTimeout(() => {
            this.playerBar.classList.add('active');
        }, 10);
    }

    closePlayer() {
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio = null;
        }

        if (this.currentBeatItem) {
            this.currentBeatItem.classList.remove('playing');
            this.currentBeatItem = null;
        }

        this.playerBar.classList.remove('active');
        setTimeout(() => {
            this.playerBar.style.display = 'none';
        }, 300);
    }

    togglePlayPause() {
        if (!this.currentAudio) return;

        if (this.currentAudio.paused) {
            this.currentAudio.play().then(() => {
                this.updatePlayPauseButton(true);
                if (this.currentBeatItem) {
                    this.currentBeatItem.classList.add('playing');
                }
            });
        } else {
            this.currentAudio.pause();
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }


async playNext() {
    let allFilteredBeats = Array.from(document.querySelectorAll('.beat-item:not(.hidden)'));

    if (!this.currentBeatItem || allFilteredBeats.length === 0) return;

    let currentIndex = allFilteredBeats.indexOf(this.currentBeatItem);

    if (currentIndex + 1 < allFilteredBeats.length) {
        const nextBeat = allFilteredBeats[currentIndex + 1];
        this.playBeat(nextBeat);
        this.scrollToBeat(nextBeat);
        return;
    }

    if (!this.hasNextPage) return;

    await this.fetchServerBeats({ append: true, preserveScroll: true, silentError: true });

    allFilteredBeats = Array.from(document.querySelectorAll('.beat-item:not(.hidden)'));
    currentIndex = allFilteredBeats.indexOf(this.currentBeatItem);

    if (currentIndex + 1 < allFilteredBeats.length) {
        const nextBeat = allFilteredBeats[currentIndex + 1];
        this.playBeat(nextBeat);
        this.scrollToBeat(nextBeat);
    }
}

    playPrevious() {
        const allFilteredBeats = Array.from(document.querySelectorAll('.beat-item:not(.hidden)'));
        
        if (!this.currentBeatItem || allFilteredBeats.length === 0) return;

        const currentIndex = allFilteredBeats.indexOf(this.currentBeatItem);
        
        if (currentIndex <= 0) return;
        
        const prevBeat = allFilteredBeats[currentIndex - 1];
        this.playBeat(prevBeat);
        this.scrollToBeat(prevBeat);
    }

    loadMoreAndPlay(beatToPlay) {
        if (!this.loadMoreBtn) {
            this.playBeat(beatToPlay);
            this.scrollToBeat(beatToPlay);
            return;
        }

        const loadUntilVisible = () => {
            if (!beatToPlay.classList.contains('beat-hidden-by-loadmore')) {
                this.playBeat(beatToPlay);
                this.scrollToBeat(beatToPlay);
                return;
            }
            
            this.loadMoreBtn.classList.add('loading');
            
            setTimeout(() => {
                this.updateVisibleBeats();
                this.loadMoreBtn.classList.remove('loading');
                
                if (beatToPlay.classList.contains('beat-hidden-by-loadmore')) {
                    loadUntilVisible();
                } else {
                    this.playBeat(beatToPlay);
                    this.scrollToBeat(beatToPlay);
                }
            }, 100);
        };
        
        loadUntilVisible();
    }

    scrollToBeat(beatElement) {
        if (!beatElement) return;
        
        setTimeout(() => {
            const rect = beatElement.getBoundingClientRect();
            const playerHeight = this.playerBar?.offsetHeight || 80;
            const beatHeight = beatElement.offsetHeight || 60;
            
            // scroll 1 beat before it goes out of view
            const topMargin = beatHeight;
            const bottomMargin = playerHeight + beatHeight + 20;
            
            const isVisible = rect.top >= topMargin && rect.bottom <= (window.innerHeight - bottomMargin);
            
            if (!isVisible) {
                beatElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 50);
    }

    updatePlayPauseButton(isPlaying) {
        if (!this.playIcon || !this.pauseIcon) return;

        this.playPauseBtn?.classList.toggle('is-playing', !!isPlaying);

        this.playIcon.style.setProperty('display', isPlaying ? 'none' : 'block', 'important');
        this.pauseIcon.style.setProperty('display', isPlaying ? 'block' : 'none', 'important');

        this.playIcon.setAttribute('aria-hidden', isPlaying ? 'true' : 'false');
        this.pauseIcon.setAttribute('aria-hidden', isPlaying ? 'false' : 'true');
    }

    updateProgress() {
        if (!this.currentAudio || !this.currentAudio.duration) return;

        const percent = (this.currentAudio.currentTime / this.currentAudio.duration) * 100;

        const customColor =
            getComputedStyle(this.progressFill).backgroundColor ||
            getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() ||
            'currentColor';

        this.progressFill.style.cssText = `
            width: ${percent}% !important;
            background-color: ${customColor} !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            height: 100% !important;
            z-index: 2147483647 !important;
        `;

        this.updateTimeDisplay();
    }

    updateTimeDisplay() {
        if (!this.currentAudio) {
            this.playerTime.textContent = "0:00 / 0:00";
            return;
        }

        const current = this.formatTime(this.currentAudio.currentTime);
        const duration = this.formatTime(this.currentAudio.duration || 0);
        this.playerTime.textContent = `${current} / ${duration}`;
    }

    formatTime(seconds) {
        if (isNaN(seconds)) return '0:00';

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    updatePlayerUI(beatItem) {
        const title = beatItem.querySelector('.beat-title')?.textContent || '';
        const coverImgSrc = beatItem.querySelector('.beat-cover img')?.src;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const productHandle = beatItem.dataset.productId || '';
        const productUrl =
            beatItem.dataset.productUrl ||
            (productHandle ? this.buildLocalizedUrl(`products/${productHandle}`) : '#');

        this.playerTrackName.textContent = title;

        const playerAlbumCover = document.getElementById('playerAlbumCover');

        if (coverImgSrc) {
            playerAlbumCover.innerHTML = `<img src="${coverImgSrc}" alt="${title}">`;
        } else {
            playerAlbumCover.innerHTML = '<div class="player-album-cover-placeholder"></div>';
        }

        if (this.playerCartBtn && price) {
            if (this.playerCartPrice) {
                this.playerCartPrice.textContent = price;
            }

            this.playerCartBtn.style.display = 'flex';

            if (this.playerCartBtn.matches('a')) {
                this.playerCartBtn.setAttribute('href', productUrl);
                this.playerCartBtn.setAttribute('aria-label', `Buy ${title}`);
            }
        }

        this.currentProductData = {
            productHandle: productHandle,
            productUrl: productUrl,
            title: title,
            coverImgSrc: coverImgSrc,
            price: price
        };
    }
    
    updateTooltip(e) {
        if (!this.currentAudio || !this.currentAudio.duration || !this.progressTooltip) return;
    
        const rect = this.progressBar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        const hoverTime = percent * this.currentAudio.duration;
    
        this.progressTooltip.textContent = this.formatTime(hoverTime);
        this.progressTooltip.style.left = `${x}px`;
        this.progressTooltip.style.opacity = '1';
        
        // Appliquer les couleurs du bouton panier au tooltip
        if (this.playerCartBtn) {
            const computedStyle = window.getComputedStyle(this.playerCartBtn);
            const backgroundColor = computedStyle.backgroundColor;
            const textColor = computedStyle.color;
            
            this.progressTooltip.style.backgroundColor = backgroundColor;
            this.progressTooltip.style.color = textColor;
        }
    }

    hideTooltip() {
        if (!this.progressTooltip) return;
        this.progressTooltip.style.opacity = '0';
    }

    async showLicenseModal(beatItem) {
        const productHandle = beatItem.dataset.productId;
        const variantId = beatItem.dataset.variantId;
        const title = beatItem.querySelector('.beat-title').textContent;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const coverImg = beatItem.querySelector('.beat-cover img');

        if (!productHandle) {
            this.showNotification('Erreur: Produit non trouvé', 'error');
            return;
        }

        this.licenseModal.style.display = 'block';
        document.body.style.overflow = 'hidden';

        const modalProductInfo = document.getElementById('modalProductInfo');
        modalProductInfo.innerHTML = `
            <div class="license-modal-product-info">
                ${
                    coverImg
                        ? `<img src="${coverImg.src}" alt="${title}" class="license-modal-product-cover">`
                        : '<div class="license-modal-product-cover license-modal-product-cover--placeholder"></div>'
                }
                <div>
                    <h3 class="license-modal-product-title">${title}</h3>
                    <p class="license-modal-product-price">Prix de base: ${price}</p>
                </div>
            </div>
        `;

        try {
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();

            const licenseOptions = document.getElementById('licenseOptions');
            licenseOptions.innerHTML = '';

            product.variants.forEach(variant => {
                const option = document.createElement('div');
                option.className = 'license-option';
                option.dataset.variantId = variant.id;

                const licenseName = variant.title || 'Licence Standard';
                const licensePrice = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(variant.price / 100);

                option.innerHTML = `
                    <div class="license-option-header">
                        <span class="license-option-name">${licenseName}</span>
                        <span class="license-option-price">${licensePrice}</span>
                    </div>
                    <div class="license-option-description">
                        ${this.getLicenseDescription(licenseName)}
                    </div>
                    ${!variant.available ? '<p class="license-option-unavailable">Non disponible</p>' : ''}
                `;

                if (!variant.available) {
                    option.classList.add('disabled');
                }

                option.addEventListener('click', () => {
                    if (variant.available) {
                        document.querySelectorAll('.license-option').forEach(opt => opt.classList.remove('selected'));
                        option.classList.add('selected');
                        this.selectedLicense = variant;
                    }
                });

                licenseOptions.appendChild(option);
            });

            const addButton = document.createElement('button');
            addButton.className = 'license-add-btn';
            addButton.textContent = 'Ajouter au panier';
            addButton.addEventListener('click', () => this.addToCart());
            licenseOptions.appendChild(addButton);

        } catch (error) {
            console.error('Erreur chargement produit:', error);
            document.getElementById('licenseOptions').innerHTML = '<p class="error-message">Erreur lors du chargement des licences</p>';
        }

        const closeModal = document.getElementById('closeModal');
        const modalOverlay = this.licenseModal.querySelector('.license-modal-overlay');

        const closeHandler = () => {
            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;
        };

        closeModal.onclick = closeHandler;
        modalOverlay.onclick = closeHandler;
    }

    getLicenseDescription(licenseName) {
        const descriptions = {
            'Basic': 'Usage personnel uniquement, pas de revente',
            'Premium': 'Usage commercial, jusqu\'à 10,000 copies',
            'Exclusive': 'Droits exclusifs, le beat sera retiré de la vente',
            'Standard': 'Licence d\'utilisation musicale'
        };

        for (const [key, desc] of Object.entries(descriptions)) {
            if (licenseName.toLowerCase().includes(key.toLowerCase())) {
                return desc;
            }
        }

        return 'Licence d\'utilisation musicale';
    }

    async addToCart() {
        if (!this.selectedLicense) {
            alert('Veuillez sélectionner une licence');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('items[0][id]', this.selectedLicense.id);
            formData.append('items[0][quantity]', '1');
            formData.append('sections', 'cart-drawer,cart-icon-bubble');
            formData.append('sections_url', window.location.pathname + window.location.search);

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Erreur lors de l\'ajout au panier');
            }

            const data = await response.json();

            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;

            this.showShopifyNotification(data);

        } catch (error) {
            console.error('Erreur:', error);
            this.showNotification('Erreur lors de l\'ajout au panier', 'error');
        }
    }

    showShopifyNotification(data) {
        const cartDrawer = document.querySelector('cart-drawer');

        const parsedState = {
            key: data.items?.[0]?.key,
            sections: data.sections || {}
        };

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(parsedState);
            return;
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
            this.updateCartBubble(data.sections['cart-icon-bubble']);
        }

        const productTitle = data.items?.[0]?.product_title || 'Produit';
        this.showNotification(`"${productTitle}" ajouté au panier!`, 'success');
    }


    updateCartBubble(bubbleHtml) {
        if (!bubbleHtml) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = bubbleHtml;

        const containerSelectors = ['#cart-icon-bubble', '.cart-icon-bubble', '.header__icon--cart'];
        for (const selector of containerSelectors) {
            const newContainer = tempDiv.querySelector(selector);
            const existingContainer = document.querySelector(selector);

            if (newContainer && existingContainer && existingContainer.parentNode) {
                existingContainer.parentNode.replaceChild(newContainer.cloneNode(true), existingContainer);
                return;
            }
        }

        const newCountBubble = tempDiv.querySelector('.cart-count-bubble');
        const existingCountBubbles = document.querySelectorAll('.cart-count-bubble');

        if (newCountBubble) {
            existingCountBubbles.forEach((bubble) => {
                if (bubble.parentNode) {
                    bubble.parentNode.replaceChild(newCountBubble.cloneNode(true), bubble);
                }
            });
        } else {
            existingCountBubbles.forEach((bubble) => bubble.remove());
        }
    }

    showNotification(message, type = 'success') {
        const notification = document.createElement('div');
        notification.className = `beat-notification beat-notification--${type}`;
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.add('show');
        }, 10);

        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => {
                notification.remove();
            }, 300);
        }, 3000);
    }

    handleDownload(beatItem) {
        
        const popupConfig = document.getElementById('popup-free-download-config');
        if (!popupConfig) {
            this.showNotification('Configuration du popup non trouvée', 'error');
            return;
        }

        const title = (beatItem.querySelector('.beat-title')?.textContent || '').trim()
            || (beatItem.dataset.title || '').trim();
        const coverImg = beatItem.dataset.img || beatItem.querySelector('.beat-cover img')?.src;
        const audioUrl = beatItem.querySelector('.beat-audio').dataset.src;

        if (!audioUrl) {
            this.showNotification('Pas de fichier audio disponible pour le téléchargement', 'error');
            return;
        }

        this.showDownloadPopup({
            title: popupConfig.dataset.popupTitle + ' : ' + title,
            subtitle: popupConfig.dataset.popupSubtitle,
            buttonText: popupConfig.dataset.popupButton,
            coverImg: coverImg,
            beatTitle: title,
            audioUrl: audioUrl,
            labels: {
                firstName: popupConfig.dataset.firstNameLabel || 'First name',
                lastName: popupConfig.dataset.lastNameLabel || 'Name',
                email: popupConfig.dataset.emailLabel || 'Email address',
                marketingConsent: popupConfig.dataset.marketingConsentLabel || 'I agree to receive information by email.',
                copyrightConsent: popupConfig.dataset.copyrightConsentLabel || 'I understand that releasing a song without a lease result in copyright infringement claims.'
            }
        });
    }

    getDownloadPopupHeader() {
        return document.querySelector('.section-header');
    }

    getDownloadPopupHeaderHeight() {
        const header = this.getDownloadPopupHeader();
        if (!header) return 0;

        const headerWrapper =
            header.querySelector('.header-wrapper') ||
            document.querySelector('.header-wrapper') ||
            document.querySelector('sticky-header');

        return Math.max(
            0,
            Math.round(
                headerWrapper?.getBoundingClientRect().height ||
                headerWrapper?.offsetHeight ||
                header.getBoundingClientRect().height ||
                header.offsetHeight ||
                0
            )
        );
    }

    revealHeaderForDownloadPopup() {
        const header = this.getDownloadPopupHeader();
        if (!header) return;

        const headerHeight = this.getDownloadPopupHeaderHeight();

        if (headerHeight > 0) {
            document.documentElement.style.setProperty('--free-download-popup-top', `${headerHeight}px`);
            document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
            document.documentElement.style.setProperty('--free-download-layout-height', `${window.innerHeight}px`);
        }

        header.classList.add('shopify-section-header-sticky', 'free-download-header-open');
        header.classList.remove('shopify-section-header-hidden');
    }

    releaseHeaderForDownloadPopup() {
        const header = this.getDownloadPopupHeader();
        if (!header) return;

        header.classList.remove('free-download-header-open');
        document.documentElement.style.removeProperty('--free-download-popup-top');
    }

    bindDownloadPopupTouchLock() {
        const popup = document.getElementById('free-download-popup');
        if (!popup) return;

        this.downloadPopupTouchStartY = 0;

        this.downloadPopupTouchStartHandler = (event) => {
            this.downloadPopupTouchStartY = event.touches?.[0]?.clientY || 0;
        };

        this.downloadPopupTouchMoveHandler = (event) => {
            const scroller = event.target.closest('.free-download-popup__content');

            if (!scroller || !popup.contains(event.target)) {
            event.preventDefault();
            return;
            }

            if (scroller.scrollHeight <= scroller.clientHeight) {
            event.preventDefault();
            return;
            }

            const currentY = event.touches?.[0]?.clientY || 0;
            const deltaY = currentY - this.downloadPopupTouchStartY;

            const atTop = scroller.scrollTop <= 0;
            const atBottom = Math.ceil(scroller.scrollTop + scroller.clientHeight) >= scroller.scrollHeight;

            if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
            event.preventDefault();
            }
        };

        popup.addEventListener('touchstart', this.downloadPopupTouchStartHandler, { passive: true });
        popup.addEventListener('touchmove', this.downloadPopupTouchMoveHandler, { passive: false });
        }

        unbindDownloadPopupTouchLock() {
        const popup = document.getElementById('free-download-popup');
        if (!popup) return;

        if (this.downloadPopupTouchStartHandler) {
            popup.removeEventListener('touchstart', this.downloadPopupTouchStartHandler);
        }

        if (this.downloadPopupTouchMoveHandler) {
            popup.removeEventListener('touchmove', this.downloadPopupTouchMoveHandler);
        }

        this.downloadPopupTouchStartHandler = null;
        this.downloadPopupTouchMoveHandler = null;
        this.downloadPopupTouchStartY = 0;
        }


        syncDownloadPopupBackgroundHeight(popup = document.getElementById('free-download-popup')) {
            const content = popup?.querySelector('.free-download-popup__content');
            if (!popup || !content) return;

            const bgHeight = Math.ceil(
                Math.max(
                    content.scrollHeight,
                    content.clientHeight,
                    content.offsetHeight
                )
            );

            popup.style.setProperty('--free-download-bg-height', `${bgHeight}px`);
        }



        bindDownloadPopupKeyboardLock() {
            const popup = document.getElementById('free-download-popup');
            if (!popup || this.downloadPopupKeyboardFocusHandler) return;

            const root = document.documentElement;
            const now = () => (window.performance?.now ? window.performance.now() : Date.now());

            const isTextInput = (element) => {
                return element?.matches?.(
                    '.free-download-popup input[type="text"], ' +
                    '.free-download-popup input[type="email"], ' +
                    '.free-download-popup textarea'
                );
            };

            const getLockedLayoutHeight = () => {
                const lockedLayoutHeight = parseFloat(
                    root.style.getPropertyValue('--free-download-layout-height')
                );

                if (Number.isFinite(lockedLayoutHeight) && lockedLayoutHeight > 0) {
                    return Math.round(lockedLayoutHeight);
                }

                return Math.round(window.innerHeight || root.clientHeight || 0);
            };

            const getViewportMetrics = () => {
                const layoutHeight = getLockedLayoutHeight();
                const viewport = window.visualViewport;

                let visualBottom = layoutHeight;

                if (viewport) {
                    visualBottom = Math.round(
                        (viewport.height || layoutHeight) + (viewport.offsetTop || 0)
                    );

                    visualBottom = Math.max(0, Math.min(layoutHeight, visualBottom));
                }

                return {
                    layoutHeight,
                    visualBottom: visualBottom || layoutHeight,
                    keyboardOffset: Math.max(0, layoutHeight - visualBottom)
                };
            };

            const applyPopupViewport = (useVisualViewport = false) => {
                const { layoutHeight, visualBottom, keyboardOffset } = getViewportMetrics();
                const visibleHeight = useVisualViewport ? visualBottom : layoutHeight;

                if (!root.style.getPropertyValue('--free-download-layout-height')) {
                    root.style.setProperty('--free-download-layout-height', `${layoutHeight}px`);
                }

                root.style.setProperty('--free-download-visual-height', `${visibleHeight || layoutHeight}px`);
                root.style.setProperty('--free-download-keyboard-offset', `${keyboardOffset}px`);

                this.syncDownloadPopupBackgroundHeight?.(popup);
            };

            const updatePopupViewport = () => {
                const keyboardMode =
                    root.classList.contains('free-download-keyboard-lock') ||
                    isTextInput(document.activeElement);

                applyPopupViewport(keyboardMode);
            };

            const schedulePopupViewportUpdate = () => {
                if (this.downloadPopupViewportFrame) {
                    window.cancelAnimationFrame(this.downloadPopupViewportFrame);
                }

                this.downloadPopupViewportFrame = window.requestAnimationFrame(() => {
                    this.downloadPopupViewportFrame = null;
                    if (!document.body.contains(popup)) return;
                    updatePopupViewport();
                });
            };

            const scheduleFollowUpViewportUpdates = () => {
                [80, 160, 260, 420, 560].forEach((delay) => {
                    window.setTimeout(schedulePopupViewportUpdate, delay);
                });
            };

            const settleKeyboardClose = (startedAt) => {
                if (isTextInput(document.activeElement)) return;

                const { keyboardOffset } = getViewportMetrics();
                const elapsed = now() - startedAt;

                schedulePopupViewportUpdate();

                if (keyboardOffset > 8 && elapsed < 700) {
                    this.downloadPopupKeyboardBlurTimer = window.setTimeout(() => {
                        settleKeyboardClose(startedAt);
                    }, 70);
                    return;
                }

                root.classList.remove('free-download-keyboard-lock');
                document.body.classList.remove('free-download-keyboard-lock');
                popup.classList.remove('free-download-popup--keyboard', 'free-download-popup--keyboard-closing');

                applyPopupViewport(false);
            };

            this.downloadPopupKeyboardFocusHandler = (event) => {
                if (!isTextInput(event.target)) return;

                window.clearTimeout(this.downloadPopupKeyboardBlurTimer);

                popup.classList.remove('free-download-popup--keyboard-closing');

                root.classList.add('free-download-keyboard-lock');
                document.body.classList.add('free-download-keyboard-lock');
                popup.classList.add('free-download-popup--keyboard');

                schedulePopupViewportUpdate();
                scheduleFollowUpViewportUpdates();
                window.setTimeout(() => {
                    const scroller = popup.querySelector('.free-download-popup__content');
                    const field = event.target.closest('.popup-field') || event.target;

                    if (scroller && field) {
                        const targetTop = Math.max(
                            0,
                            field.offsetTop - scroller.clientHeight * 0.32
                        );

                        scroller.scrollTo({
                            top: targetTop,
                            behavior: 'smooth'
                        });
                    }

                    this.syncDownloadPopupBackgroundHeight(popup);
                }, 180);
            };

            this.downloadPopupKeyboardBlurHandler = () => {
                window.clearTimeout(this.downloadPopupKeyboardBlurTimer);

                popup.classList.add('free-download-popup--keyboard-closing');

                const startedAt = now();

                schedulePopupViewportUpdate();
                scheduleFollowUpViewportUpdates();

                this.downloadPopupKeyboardBlurTimer = window.setTimeout(() => {
                    settleKeyboardClose(startedAt);
                }, 140);
            };

            this.downloadPopupViewportHandler = () => {
                if (!root.classList.contains('free-download-scroll-locked')) return;
                schedulePopupViewportUpdate();
            };

            popup.addEventListener('focusin', this.downloadPopupKeyboardFocusHandler);
            popup.addEventListener('focusout', this.downloadPopupKeyboardBlurHandler);

            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this.downloadPopupViewportHandler, { passive: true });
                window.visualViewport.addEventListener('scroll', this.downloadPopupViewportHandler, { passive: true });
            }

            applyPopupViewport(false);
    }
    

    unbindDownloadPopupKeyboardLock() {
        const popup = document.getElementById('free-download-popup');

        if (popup && this.downloadPopupKeyboardFocusHandler) {
            popup.removeEventListener('focusin', this.downloadPopupKeyboardFocusHandler);
        }

        if (popup && this.downloadPopupKeyboardBlurHandler) {
            popup.removeEventListener('focusout', this.downloadPopupKeyboardBlurHandler);
        }

        if (window.visualViewport && this.downloadPopupViewportHandler) {
            window.visualViewport.removeEventListener('resize', this.downloadPopupViewportHandler);
            window.visualViewport.removeEventListener('scroll', this.downloadPopupViewportHandler);
        }

        document.documentElement.classList.remove('free-download-keyboard-lock');
        document.body.classList.remove('free-download-keyboard-lock');

        window.clearTimeout(this.downloadPopupKeyboardBlurTimer);
        this.downloadPopupKeyboardBlurTimer = null;

        if (this.downloadPopupViewportFrame) {
            window.cancelAnimationFrame(this.downloadPopupViewportFrame);
            this.downloadPopupViewportFrame = null;
        }

        popup?.classList.remove('free-download-popup--keyboard', 'free-download-popup--keyboard-closing');

        document.documentElement.style.removeProperty('--free-download-visual-height');
        document.documentElement.style.removeProperty('--free-download-keyboard-offset');

        this.downloadPopupKeyboardFocusHandler = null;
        this.downloadPopupKeyboardBlurHandler = null;
        this.downloadPopupViewportHandler = null;
    }

    lockDownloadPopupScroll() {
        const root = document.documentElement;

        if (root.classList.contains('free-download-scroll-locked')) return;

        this.downloadPopupScrollY =
            window.pageYOffset ||
            root.scrollTop ||
            document.body.scrollTop ||
            0;

        this.downloadPopupPreviousHtmlScrollBehavior = root.style.scrollBehavior;
        this.downloadPopupPreviousBodyScrollBehavior = document.body.style.scrollBehavior;

        this.downloadPopupPreviousBodyStyle = {
            position: document.body.style.position,
            top: document.body.style.top,
            left: document.body.style.left,
            right: document.body.style.right,
            width: document.body.style.width,
            overflow: document.body.style.overflow
        };

        root.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';

        root.style.setProperty('--free-download-scroll-lock-top', `-${this.downloadPopupScrollY}px`);
        root.style.setProperty('--free-download-layout-height', `${window.innerHeight}px`);
        root.style.setProperty('--free-download-visual-height', `${window.innerHeight}px`);
        root.style.setProperty('--free-download-keyboard-offset', '0px');



        this.revealHeaderForDownloadPopup();

        root.classList.add('free-download-scroll-locked', 'free-download-ui-open');
        document.body.classList.add('free-download-scroll-locked', 'free-download-ui-open');

        document.body.style.overflow = 'hidden';

        if (!this.isAppleDevice()) {
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.downloadPopupScrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
        }

        this.bindDownloadPopupTouchLock();
        this.bindDownloadPopupKeyboardLock();
    }

    unlockDownloadPopupScroll() {
        const root = document.documentElement;
        this.unbindDownloadPopupTouchLock();
        this.unbindDownloadPopupKeyboardLock();

        if (!root.classList.contains('free-download-scroll-locked')) return;

        const scrollY = this.downloadPopupScrollY || 0;
        const previousBodyStyle = this.downloadPopupPreviousBodyStyle || {};

        root.classList.remove('free-download-scroll-locked', 'free-download-ui-open');
        document.body.classList.remove('free-download-scroll-locked', 'free-download-ui-open');

        document.body.style.position = previousBodyStyle.position || '';
        document.body.style.top = previousBodyStyle.top || '';
        document.body.style.left = previousBodyStyle.left || '';
        document.body.style.right = previousBodyStyle.right || '';
        document.body.style.width = previousBodyStyle.width || '';
        document.body.style.overflow = previousBodyStyle.overflow || '';

        root.style.removeProperty('--free-download-scroll-lock-top');
        root.style.removeProperty('--free-download-layout-height');
        this.releaseHeaderForDownloadPopup();

        root.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';

        if (!this.isAppleDevice()) {
            window.scrollTo(0, scrollY);
            root.scrollTop = scrollY;
            document.body.scrollTop = scrollY;
        }

        requestAnimationFrame(() => {
            if (!this.isAppleDevice()) {
                window.scrollTo(0, scrollY);
            }

            root.style.scrollBehavior = this.downloadPopupPreviousHtmlScrollBehavior || '';
            document.body.style.scrollBehavior = this.downloadPopupPreviousBodyScrollBehavior || '';

            this.downloadPopupScrollY = 0;
        });
    }

    showDownloadPopup(config) {
        const existingPopup = document.getElementById('free-download-popup');
        if (existingPopup) {
            existingPopup.remove();
            this.unlockDownloadPopupScroll();
        }

        const popup = document.createElement('div');
        popup.id = 'free-download-popup';
        const colorScheme = document.getElementById('popup-free-download-config').dataset.colorScheme || '';
        popup.className = 'free-download-popup ' + colorScheme + ' gradient';
        const popupConfig = document.getElementById('popup-free-download-config');

        // Style visuel unifié du popup Free Download.
        // Ces valeurs sont volontairement fixes pour avoir le même rendu
        // entre la collection et la page produit.
        popup.style.setProperty('--popup-image-opacity', '1');
        popup.style.setProperty('--popup-image-overlay-opacity', '0.80');
        popup.style.setProperty('--popup-image-brightness', '0.42');
        popup.style.setProperty('--free-download-card-bg', 'rgba(5, 5, 5, 0.88)');
        popup.style.setProperty('--free-download-card-border', 'rgba(255, 255, 255, 0.16)');
        popup.style.setProperty('--free-download-card-radius', '14px');

        // Subtitle: transforme "\n" en vrais retours + sécurise + <br>
        const escapeHtml = (s = '') =>
            s.replace(/[&<>"']/g, (ch) => ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            }[ch]));

        const subtitleNormalized = (config.subtitle || '').replace(/\\n/g, '\n');
        const subtitleHtml = escapeHtml(subtitleNormalized).replace(/\n/g, '<br>');
        const labels = config.labels || {};
        
        popup.innerHTML = `
            <div class="free-download-popup__content">
                <div class="free-download-popup__bg">
                    ${config.coverImg ? `<img class="free-download-popup__image" src="${config.coverImg}" alt="${config.beatTitle}" onload="this.classList.add('loaded')">` : ''}
                </div>
                
                <button class="free-download-popup__close" type="button">×</button>
                
                <div class="free-download-popup__fields">
                    <h3 class="popup-title">${config.title}</h3>
                    <p class="popup-subtitle">${subtitleHtml}</p>
                    <form class="popup-form" data-audio-url="${config.audioUrl}">
                        <div class="popup-field">
                            <label for="popup-firstname">${labels.firstName || 'First name'}</label>
                            <input type="text" id="popup-firstname" name="firstName" required>
                        </div>
                        
                        <div class="popup-field">
                            <label for="popup-lastname">${labels.lastName || 'Name'}</label>
                            <input type="text" id="popup-lastname" name="lastName" required>
                        </div>
                        
                        <div class="popup-field">
                            <label for="popup-email">${labels.email || 'Email address'}</label>
                            <input type="email" id="popup-email" name="email" required>
                        </div>
                        
                        <div class="popup-privacy">
                            <input type="checkbox" id="popup-privacy" required>
                            <label for="popup-privacy">${labels.marketingConsent || 'I agree to receive information by email.'}</label>
                        </div>
                        <div class="popup-privacy">
                            <input type="checkbox" id="popup-copyright" name="copyrightAck" required>
                            <label for="popup-copyright">
                                ${labels.copyrightConsent || 'I understand that releasing a song without a lease result in copyright infringement claims.'}
                            </label>
                        </div>
                        
                        <button type="submit" class="popup-submit">${config.buttonText}</button>
                    </form>
                </div>
            </div>
        `;

        this.revealHeaderForDownloadPopup();

        document.body.appendChild(popup);
        this.lockDownloadPopupScroll();

        const syncPopupBackgroundHeight = () => this.syncDownloadPopupBackgroundHeight(popup);

        requestAnimationFrame(syncPopupBackgroundHeight);
        window.setTimeout(syncPopupBackgroundHeight, 80);
        window.setTimeout(syncPopupBackgroundHeight, 260);

        popup
            .querySelector('.free-download-popup__image')
            ?.addEventListener('load', syncPopupBackgroundHeight, { once: true });

        const closeBtn = popup.querySelector('.free-download-popup__close');
        const form = popup.querySelector('.popup-form');
        
        closeBtn.addEventListener('click', () => this.closeDownloadPopup());
        popup.addEventListener('click', (e) => {
            if (e.target === popup) this.closeDownloadPopup();
        });

        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleDownloadSubmit(form);
        });

        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.visibility = 'visible';
        });
    }

    closeDownloadPopup() {
        const popup = document.getElementById('free-download-popup');

        if (!popup) {
            this.unlockDownloadPopupScroll();
            return;
        }

        popup.style.opacity = '0';
        popup.style.visibility = 'hidden';

        setTimeout(() => {
            popup.remove();
            this.unlockDownloadPopupScroll();
        }, 300);
    }

    async handleDownloadSubmit(form) {
        const fd = new FormData(form);
        const firstName = fd.get('firstName') || fd.get('first_name') || fd.get('popup-firstname') || '';
        const lastName  = fd.get('lastName')  || fd.get('last_name')  || fd.get('popup-lastname') || '';
        const email     = fd.get('email')     || fd.get('emailAddress') || fd.get('popup-email') || '';
        const beatTitle = (document.querySelector('.popup-title')?.innerText) || (fd.get('beatTitle')) || 'Unknown Beat';
        let audioUrl = form.dataset.audioUrl || '';
        if (audioUrl.startsWith('//')) {
            audioUrl = 'https:' + audioUrl;
        }

        const payload = {
            beatTitle: String(beatTitle || '').trim(),
            email: String(email || '').trim(),
            name: (String(firstName || '') + ' ' + String(lastName || '')).trim(),
            lang: (document.documentElement.lang || navigator.language || navigator.userLanguage || 'en').toLowerCase(),
            downloadUrl: String(audioUrl || '').trim()
        };

        console.log('[free-download] payload envoyé à Render :', payload);

        const GAS_URL = 'https://beat-search-proxy.onrender.com/api/free-download';

        form.classList.add('is-submitting');
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        this.closeDownloadPopup();
        this.showNotification("Thank you, you will receive an email shortly", "success");

        try {
            const response = await fetch(GAS_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const text = await response.text();
            console.log('[free-download] réponse Render :', response.status, text);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status} - ${text}`);
            }

        } catch (err) {
            console.error('[free-download] request failed', err);
            this.showNotification("Une erreur est survenue. Réessayez plus tard.", "error");
        } finally {
            form.classList.remove('is-submitting');
            if (submitBtn) submitBtn.disabled = false;
        }
    }

    initVolumeControls() {
        if (!this.volumeBtn || !this.volumeSliderTrack) return;

        const volumeControl = this.volumeBtn.closest('.player-volume-control');
        let sliderShowTimeout;

        const preventDefaults = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const clearStuckStates = () => {
            this.volumeBtn.style.transform = '';
            this.volumeBtn.style.backgroundColor = '';
            this.volumeBtn.blur(); 

            this.volumeBtn.offsetHeight;
        };

        const setVolumeFromEvent = (e) => {
            if (!this.currentAudio) return;
            
            const trackRect = this.volumeSliderTrack.getBoundingClientRect();
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            let volumePercent = ((trackRect.bottom - clientY) / trackRect.height) * 100;
            volumePercent = Math.max(0, Math.min(100, volumePercent));
            
            this.setVolume(volumePercent);
        };

        if (!this.isMobileDevice()) {
            this.volumeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                clearStuckStates();
                
                if (!this.currentAudio) return;
                
                if (this.currentAudio.volume > 0) {
                    this.lastVolume = this.currentAudio.volume * 100;
                    this.setVolume(0);
                } else {
                    const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
                    this.setVolume(volumeToRestore);
                }
            });

            volumeControl.addEventListener('mouseenter', () => {
                clearTimeout(sliderShowTimeout);
                volumeControl.classList.add('show-slider');
            });

            volumeControl.addEventListener('mouseleave', () => {
                sliderShowTimeout = setTimeout(() => {
                    volumeControl.classList.remove('show-slider');
                }, 200);
            });

        } else {
            
            this.volumeBtn.addEventListener('touchstart', preventDefaults, { passive: false });
            this.volumeBtn.addEventListener('touchend', (e) => {
                preventDefaults(e);
                clearStuckStates();
                
                if (volumeControl.classList.contains('show-slider')) {
                    volumeControl.classList.remove('show-slider');
                } else {
                    volumeControl.classList.add('show-slider');
                }
            }, { passive: false });

            document.addEventListener('touchstart', (e) => {
                if (!volumeControl.contains(e.target)) {
                    volumeControl.classList.remove('show-slider');
                }
            });
        }

        let isDragging = false;

        const startDrag = (e) => {
            isDragging = true;
            this.isDraggingVolume = true;
            setVolumeFromEvent(e);
            
            if (e.type === 'touchstart') {
                preventDefaults(e);
            }
        };

        const moveDrag = (e) => {
            if (isDragging) {
                setVolumeFromEvent(e);
                if (e.type === 'touchmove') {
                    preventDefaults(e);
                }
            }
        };

        const endDrag = () => {
            isDragging = false;
            this.isDraggingVolume = false;
        };

        this.volumeSliderTrack.addEventListener('mousedown', startDrag);
        this.volumeSliderTrack.addEventListener('touchstart', startDrag, { passive: false });
        
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('touchmove', moveDrag, { passive: false });
        
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);

        this.setVolume(this.getStoredVolume());
    }
    
    setVolume(volume) {
        const newVolume = Math.max(0, Math.min(100, volume));
        
        if (newVolume > 0) {
            this.lastVolume = newVolume;
        }

        if (this.volumeSliderFill) {
            const volumeFillColor =
                getComputedStyle(this.volumeSliderFill).backgroundColor ||
                getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() ||
                'currentColor';
            this.volumeSliderFill.style.cssText = `
                height: ${newVolume}% !important;
                background-color: ${volumeFillColor} !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                position: absolute !important;
                bottom: 0 !important;
                left: 0 !important;
                width: 100% !important;
                z-index: 2147483647 !important;
            `;
        }

        this.updateVolumeIcon(newVolume);
        this.persistVolume(newVolume);

        if (!this.currentAudio) return;

        this.currentAudio.volume = newVolume / 100;
        this.isMuted = (newVolume === 0);
    }
    
    updateVolumeIcon(volume) {
        if (!this.volumeIcons.normal || !this.volumeIcons.mute) {
            return;
        }
        
        this.volumeIcons.normal.style.display = 'none';
        this.volumeIcons.mute.style.display = 'none';

        if (volume === 0) {
            this.volumeIcons.mute.style.display = 'block';
        } else {
            this.volumeIcons.normal.style.display = 'block';
        }
    }

    // keyboard shortcuts
    initKeyboardControls() {
        document.addEventListener('keydown', (e) => {
            if (this.isInputFocused()) return;
            if (!this.currentAudio) return;

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    this.seekForward();
                    break;
                    
                case 'ArrowLeft':
                    e.preventDefault();
                    this.seekBackward();
                    break;
                    
                case ' ':
                    e.preventDefault();
                    this.togglePlayPause();
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    this.playPrevious();
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    this.playNext();
                    break;

                case 'n':
                case 'N':
                    e.preventDefault();
                    this.playNext();
                    break;

                case 'p':
                case 'P':
                    e.preventDefault();
                    this.playPrevious();
                    break;

                case 'm':
                case 'M':
                    e.preventDefault();
                    this.toggleMute();
                    break;
            }
        });
    }

    isInputFocused() {
        const activeElement = document.activeElement;
        const tagName = activeElement?.tagName?.toLowerCase();
        return tagName === 'input' || tagName === 'textarea' || activeElement?.isContentEditable;
    }

    seekForward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        const newTime = Math.min(this.currentAudio.currentTime + this.seekStep, this.currentAudio.duration);
        this.currentAudio.currentTime = newTime;
    }

    seekBackward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        const newTime = Math.max(this.currentAudio.currentTime - this.seekStep, 0);
        this.currentAudio.currentTime = newTime;
    }

    toggleMute() {
        if (!this.currentAudio) return;
        
        if (this.currentAudio.volume > 0) {
            this.lastVolume = this.currentAudio.volume * 100;
            this.setVolume(0);
        } else {
            const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
            this.setVolume(volumeToRestore);
        }
        this.showVolumeSlider();
    }

    toggleAutoplay() {
        this.autoplayEnabled = !this.autoplayEnabled;
        const status = this.autoplayEnabled ? 'activé' : 'désactivé';
        this.showNotification(`Autoplay ${status}`, 'success');
    }

    showVolumeSlider() {
        const volumeControl = this.volumeBtn?.closest('.player-volume-control');
        if (!volumeControl) return;

        if (this.volumeSliderTimeout) {
            clearTimeout(this.volumeSliderTimeout);
        }

        volumeControl.classList.add('show-slider');

        this.volumeSliderTimeout = setTimeout(() => {
            volumeControl.classList.remove('show-slider');
        }, 1500);
    }

    // scroll on player = volume control
    initScrollVolumeControl() {
        if (!this.playerBar) return;

        this.playerBar.addEventListener('wheel', (e) => {
            if (!this.currentAudio) return;
            
            e.preventDefault();
            
            const currentVolume = this.currentAudio.volume * 100;
            
            if (e.deltaY < 0) {
                this.setVolume(Math.min(currentVolume + 5, 100));
            } else {
                this.setVolume(Math.max(currentVolume - 5, 0));
            }
            
            this.showVolumeSlider();
        }, { passive: false });
    }

    initProductPreview() {
        const previewBeatItem = document.getElementById('beatProductItem');
        const overlayBtn = document.getElementById('beatProductOverlay');
        const mediaWrapper = document.querySelector('.product__media-wrapper');

        if (!previewBeatItem || !overlayBtn || !mediaWrapper) return;

        const playPreview = () => {
            // Si c’est déjà le preview du produit → toggle play/pause (pause OU reprise)
            if (this.currentBeatItem === previewBeatItem && this.currentAudio) {
                this.togglePlayPause();
                return;
            }

            // Sinon, force la preview du produit
            this.playBeat(previewBeatItem);
        };

        overlayBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            playPreview();
        });

        // Optionnel: clic sur l’image (sans bloquer le zoom/modal)
        mediaWrapper.addEventListener('click', (e) => {
            // évite de déclencher quand on clique sur les contrôles du slider
            if (e.target.closest('button, a, .slider-button')) return;
            playPreview();
        });

        // Sync UI overlay (classe 'playing' déjà gérée par ton code)
        const syncOverlay = () => {
            overlayBtn.classList.toggle('is-playing', previewBeatItem.classList.contains('playing'));
        };

        new MutationObserver(syncOverlay).observe(previewBeatItem, {
            attributes: true,
            attributeFilter: ['class'],
        });
        syncOverlay();
    }

}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.getElementById('beatPlayerBar')) return; // pas sur la page beat-store => on skip
  try {
    if (!window.__beatStoreInstance) window.__beatStoreInstance = new BeatStore();
  } catch (e) {
    console.error('BeatStore init failed:', e);
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const norm = (s) =>
    (s || "")
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

  const translationsEl = document.getElementById("license-translations");

  let LICENSE_TEXT = {};
  if (translationsEl) {
    try {
      LICENSE_TEXT = JSON.parse(translationsEl.textContent);
    } catch (e) {
      console.error("Invalid license translations JSON:", e);
      LICENSE_TEXT = {};
    }
  }

  const getText = (value) => {
    const key = norm(value);
    if (LICENSE_TEXT[key]) return LICENSE_TEXT[key];

    for (const k of Object.keys(LICENSE_TEXT)) {
      if (key.includes(k)) return LICENSE_TEXT[k];
    }

    return "";
  };

  document.addEventListener(
    "change",
    (e) => {
        const picker = e.target.closest("[data-license-picker]");
        if (!picker || !e.target.matches("input, select")) return;

        const note = picker.querySelector("[data-license-selection-note]");
        if (!note) return;

        const value =
        picker.querySelector('input[type="radio"]:checked')?.value ||
        picker.querySelector("select")?.value ||
        "";

        const text = getText(value);

        window.clearTimeout(picker._licensePickerFadeTimer);

        picker.classList.add("is-changing");
        note.classList.add("is-changing");

        picker._licensePickerFadeTimer = window.setTimeout(() => {
        if (text) note.textContent = text;

        picker.classList.remove("is-changing");
        note.classList.remove("is-changing");
        }, 140);
    },
    true
    );
});

document.addEventListener("DOMContentLoaded", () => {
  const btn = document.querySelector("[data-product-free-download]");
  if (!btn) return;

  btn.addEventListener("click", (e) => {
    e.preventDefault();

    const audioUrl = btn.dataset.audio;
    if (!audioUrl) return;

    const cfg = document.getElementById("popup-free-download-config");

    // si BeatStore est dispo, on réutilise le popup existant
    if (window.__beatStoreInstance && typeof window.__beatStoreInstance.showDownloadPopup === "function" && cfg) {
      const title = btn.dataset.title || document.title;
      window.__beatStoreInstance.showDownloadPopup({
        title: (cfg.dataset.popupTitle || "Free download") + " : " + title,
        subtitle: cfg.dataset.popupSubtitle || "",
        buttonText: cfg.dataset.popupButton || "Send",
        coverImg: btn.dataset.img || "",
        beatTitle: title,
        audioUrl: audioUrl,
        labels: {
            firstName: cfg.dataset.firstNameLabel || "First name",
            lastName: cfg.dataset.lastNameLabel || "Name",
            email: cfg.dataset.emailLabel || "Email address",
            marketingConsent: cfg.dataset.marketingConsentLabel || "I agree to receive information by email.",
            copyrightConsent: cfg.dataset.copyrightConsentLabel || "I understand that releasing a song without a lease result in copyright infringement claims."
        }
        });
      return;
    }

    // fallback : ouvre le fichier
    window.open(audioUrl, "_blank");
  });
});

// --- Fix scroll "License guide" quand Related Products charge en AJAX ---
(() => {
  const LINK_SELECTOR = 'a.license-guide-link';
  const TARGET_ID = 'licensing-info';
  const RELATED_SELECTOR = 'product-recommendations.related-products';

  const getHeaderOffset = () => {
    const header =
      document.querySelector('sticky-header') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('header');
    return header ? header.getBoundingClientRect().height : 0;
  };

  const scrollToLicensing = (behavior = 'smooth') => {
    const target = document.getElementById(TARGET_ID);
    if (!target) return;

    const top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      getHeaderOffset() -
      16;

    window.scrollTo({ top, behavior });
  };

  const relatedHasContent = (relatedEl) => {
    if (!relatedEl) return false;
    return !!relatedEl.querySelector(
      '.related-products__heading, .product-grid, .grid__item, li, .card-wrapper'
    );
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest(LINK_SELECTOR);
    if (!link) return;

    const target = document.getElementById(TARGET_ID);
    if (!target) return;

    e.preventDefault();

    // 1) Scroll immédiat
    scrollToLicensing('smooth');

    // 2) Si related products n’existent pas -> rien d’autre à faire
    const related = document.querySelector(RELATED_SELECTOR);
    if (!related) return;

    // 3) Si déjà chargé -> on recale une fois (sécurité)
    if (relatedHasContent(related)) {
      requestAnimationFrame(() => scrollToLicensing('auto'));
      return;
    }

    // 4) Attendre le chargement AJAX (injection HTML) puis recaler
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try { mo.disconnect(); } catch (_) {}

      // 2 RAF pour laisser le layout se stabiliser
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToLicensing('auto')));
    };

    const mo = new MutationObserver(() => {
      const r = document.querySelector(RELATED_SELECTOR);
      if (r && relatedHasContent(r)) finish();
    });

    mo.observe(related, { childList: true, subtree: true });

    // 5) Bonus: si images/font changent la hauteur, recale un peu (max ~2s)
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => scrollToLicensing('auto'));
      ro.observe(related);
      setTimeout(() => ro.disconnect(), 2000);
    }

    // Fallback hard-stop
    setTimeout(finish, 5000);
  });
})();



